import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { useOutreachLists } from '../hooks/useOutreachLists';
import { outreachMessages } from '../outreachMessages';

const messages = defineMessages({
  newList: { id: 'app.outreach.lists.new', defaultMessage: 'New list' },
  newListName: { id: 'app.outreach.lists.newName', defaultMessage: 'Name of the new list' },
  noLists: { id: 'app.outreach.lists.none', defaultMessage: 'No list yet' },
});

interface AddToListMenuProps {
  accountId: number;
  /** Gets the workspace contact uuids to add (Find first adds the directory contacts). */
  resolveContacts: () => Promise<string[]>;
  onDone?: (count: number) => void;
  disabled?: boolean;
}

/** "Add to a list" (design outreach c1, q1): the lists of the artist and "New list". */
const AddToListMenuComponent: React.FC<AddToListMenuProps> = ({
  accountId,
  resolveContacts,
  onDone,
  disabled = false,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { lists, createList, addToList, isWriting } = useOutreachLists(accountId);

  const addTo = useCallback(
    async (listUuid: string) => {
      const workspaceContactUuids = await resolveContacts();
      if (workspaceContactUuids.length === 0) {
        return;
      }
      await addToList({ listUuid, workspaceContactUuids });
      onDone?.(workspaceContactUuids.length);
    },
    [addToList, onDone, resolveContacts]
  );

  const createAndAdd = useCallback(async () => {
    // eslint-disable-next-line no-alert -- one short name; a dialog of its own is more than this needs
    const name = window.prompt(intl.formatMessage(messages.newListName))?.trim() ?? '';
    if (name === '') {
      return;
    }
    const { listUuid } = await createList(name);
    await addTo(listUuid);
  }, [addTo, createList, intl]);

  const items = useMemo(
    () =>
      lists.map((list) => (
        <DropdownItem key={list.listUuid} onClick={() => addTo(list.listUuid).catch(console.error)}>
          {list.name}
        </DropdownItem>
      )),
    [addTo, lists]
  );

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen((open) => !open)}>
      <DropdownToggle caret outline color="secondary" size="sm" disabled={disabled || isWriting}>
        {intl.formatMessage(outreachMessages.addToList)}
      </DropdownToggle>
      <DropdownMenu>
        {lists.length === 0 ? <DropdownItem disabled>{intl.formatMessage(messages.noLists)}</DropdownItem> : items}
        <DropdownItem divider />
        <DropdownItem onClick={() => createAndAdd().catch(console.error)}>
          {intl.formatMessage(messages.newList)}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const AddToListMenu = TypedMemo(AddToListMenuComponent);
